import { Thread_Activity_Type_Enum, Thread_Type_Enum } from '@gql'

// Resolutions a thread can offer, i.e. the creation buttons of its action bar.
// Message activities are not resolutions: they are not created from a button.
export type ThreadResolution =
  | Thread_Activity_Type_Enum.Proposal
  | Thread_Activity_Type_Enum.Decision
  | Thread_Activity_Type_Enum.Poll
  | Thread_Activity_Type_Enum.Task
  | Thread_Activity_Type_Enum.NextStep
  | Thread_Activity_Type_Enum.Meeting
  | Thread_Activity_Type_Enum.Thread

// Which thread types offer which resolution.
// Proposal and Decision belong to governance, Task to day-to-day operations,
// the rest serves both.
const resolutionThreadTypes: Record<ThreadResolution, Thread_Type_Enum[]> = {
  [Thread_Activity_Type_Enum.Proposal]: [Thread_Type_Enum.Governance],
  [Thread_Activity_Type_Enum.Decision]: [Thread_Type_Enum.Governance],
  [Thread_Activity_Type_Enum.Task]: [Thread_Type_Enum.Tactical],
  // Next step: operations only, like Task (G2-D5). A governance tension is
  // resolved by a proposal or a policy, not by someone taking an action.
  // ⚠️ The other half of the rule — the button only exists during a meeting
  // (G2-D4) — is NOT here: it depends on where the thread is being read, not
  // on what the thread is. Putting it here would force a React state into a
  // module whose whole value is being testable without rendering anything.
  [Thread_Activity_Type_Enum.NextStep]: [Thread_Type_Enum.Tactical],
  [Thread_Activity_Type_Enum.Poll]: [
    Thread_Type_Enum.Tactical,
    Thread_Type_Enum.Governance,
  ],
  [Thread_Activity_Type_Enum.Meeting]: [
    Thread_Type_Enum.Tactical,
    Thread_Type_Enum.Governance,
  ],
  [Thread_Activity_Type_Enum.Thread]: [
    Thread_Type_Enum.Tactical,
    Thread_Type_Enum.Governance,
  ],
}

// Pure rule, isolated from React so it can be tested without rendering.
export function isResolutionOffered(
  resolution: ThreadResolution,
  threadType: Thread_Type_Enum | null | undefined
): boolean {
  // Unknown/missing type: offer everything rather than nothing.
  // Unexpected data must never deprive someone of their tools.
  if (!threadType || !Object.values(Thread_Type_Enum).includes(threadType))
    return true
  return resolutionThreadTypes[resolution].includes(threadType)
}

// The other type, used to route a resolution towards a sub-thread.
// Returns undefined for an unknown type: we never guess an opposite.
export function oppositeThreadType(
  threadType: Thread_Type_Enum | null | undefined
): Thread_Type_Enum | undefined {
  switch (threadType) {
    case Thread_Type_Enum.Tactical:
      return Thread_Type_Enum.Governance
    case Thread_Type_Enum.Governance:
      return Thread_Type_Enum.Tactical
    default:
      return undefined
  }
}
